import {
  Alert,
  Button,
  Group,
  Modal,
  NativeSelect,
  PasswordInput,
  Loader,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type {
  CiLogConnection,
  CiLogStatus,
  CiLogStatusReason,
} from "./ciLogsApi";

// Презентационные части секции «CI-логи», вынесенные из
// `CiLogsSection.tsx` ради лимита длины файла.

/** Сводка сохранённого подключения — без похода в сеть. */
export function CiLogSummary(props: {
  readonly connection: CiLogConnection;
}): ReactElement {
  const { t } = useTranslation();
  const { connection } = props;

  if (!connection.connected) {
    return <Text>{t("connections.ciLogs.summary.notConnected")}</Text>;
  }

  return (
    <Stack gap={4}>
      <Text>
        {t("connections.ciLogs.summary.provider", {
          provider: connection.provider,
        })}
      </Text>
      <Text>
        {t("connections.ciLogs.summary.host", { host: connection.host_url })}
      </Text>
      <Text>
        {connection.principal_login === null
          ? t("connections.ciLogs.summary.principalUnknown")
          : t("connections.ciLogs.summary.principal", {
              login: connection.principal_login,
            })}
      </Text>
    </Stack>
  );
}

const STATUS_REASON_KEYS: Record<CiLogStatusReason, string> = {
  host_not_allowed: "connections.ciLogs.status.reason.host_not_allowed",
  credentials_missing: "connections.ciLogs.status.reason.credentials_missing",
  credentials_rejected: "connections.ciLogs.status.reason.credentials_rejected",
  unreachable: "connections.ciLogs.status.reason.unreachable",
  provider_unknown: "connections.ciLogs.status.reason.provider_unknown",
  log_api_unsupported: "connections.ciLogs.status.reason.log_api_unsupported",
};

/** Живой отчёт о доступности (`GET /status`) + кнопка перепроверки. */
export function CiLogStatusBlock(props: {
  readonly status: CiLogStatus;
  readonly storedLogin: string | null;
  readonly onRecheck: () => void;
  readonly isFetching: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { status, storedLogin, onRecheck, isFetching } = props;

  const ready =
    status.reachable === true &&
    status.log_api_supported === true &&
    status.principal_matches !== false;

  return (
    <Stack gap="sm">
      {status.connected ? null : (
        <Alert color="yellow">
          {t("connections.ciLogs.status.notConnected")}
        </Alert>
      )}
      {ready ? (
        <Alert color="green">
          {t("connections.ciLogs.status.ok", {
            login: status.principal_login ?? "",
          })}
        </Alert>
      ) : null}
      {status.log_api_supported === false ? (
        <Alert color="yellow">
          {t("connections.ciLogs.status.logApiUnsupported")}
        </Alert>
      ) : null}
      {status.principal_matches === false ? (
        <Alert color="yellow">
          {t("connections.ciLogs.status.mismatch", {
            actual: status.principal_login ?? "",
            stored: storedLogin ?? "",
          })}
        </Alert>
      ) : null}
      {status.reachable === false && status.reason !== null ? (
        <Alert color="red">{t(STATUS_REASON_KEYS[status.reason])}</Alert>
      ) : null}
      <Group>
        <Button variant="default" onClick={onRecheck} loading={isFetching}>
          {t("connections.ciLogs.recheckButton")}
        </Button>
      </Group>
    </Stack>
  );
}

/** Подтверждение «Отключить»: статическое предупреждение. */
export function CiLogDisconnectModal(props: {
  readonly opened: boolean;
  readonly isSubmitting: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { opened, isSubmitting, onConfirm, onCancel } = props;

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={t("connections.ciLogs.disconnect.title")}
      centered
    >
      <Stack gap="md">
        <Text>{t("connections.ciLogs.disconnect.text")}</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            {t("connections.modalCancel")}
          </Button>
          <Button color="red" onClick={onConfirm} loading={isSubmitting}>
            {t("connections.modalConfirm")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Поля формы подключения. `repoHostUrl !== null` — показать кнопку
 * «подставить адрес репо-хоста» (решает секция).
 */
export function CiLogFormFields(props: {
  readonly providers: readonly string[];
  readonly provider: string;
  readonly host: string;
  readonly pat: string;
  readonly urlError: string | null;
  readonly patError: string | null;
  readonly repoHostUrl: string | null;
  readonly onProviderChange: (value: string) => void;
  readonly onHostChange: (value: string) => void;
  readonly onPatChange: (value: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { repoHostUrl, onHostChange } = props;

  return (
    <Stack gap="sm">
      <NativeSelect
        label={t("connections.ciLogs.providerLabel")}
        data={[...props.providers]}
        value={props.provider}
        onChange={(event) => {
          props.onProviderChange(event.currentTarget.value);
        }}
      />
      <TextInput
        label={t("connections.ciLogs.hostLabel")}
        placeholder={t("connections.ciLogs.hostPlaceholder")}
        value={props.host}
        onChange={(event) => {
          onHostChange(event.currentTarget.value);
        }}
        error={props.urlError}
      />
      {repoHostUrl === null ? null : (
        <Button
          variant="subtle"
          size="xs"
          onClick={() => {
            onHostChange(repoHostUrl);
          }}
        >
          {t("connections.ciLogs.useRepoHostUrl", { host: repoHostUrl })}
        </Button>
      )}
      <PasswordInput
        label={t("connections.ciLogs.patLabel")}
        description={t("connections.ciLogs.patHint")}
        value={props.pat}
        onChange={(event) => {
          props.onPatChange(event.currentTarget.value);
        }}
        error={props.patError}
      />
    </Stack>
  );
}

/** Загрузка / ошибка / отчёт живого статуса подключённой CI-системы. */
export function CiLogLiveStatus(props: {
  readonly query: UseQueryResult<CiLogStatus, Error>;
  readonly storedLogin: string | null;
  readonly onRecheck: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { query, storedLogin, onRecheck } = props;

  if (query.isPending) {
    return <Loader size="sm" />;
  }
  if (query.isError) {
    return (
      <Stack gap="xs">
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
        <Group>
          <Button
            variant="default"
            onClick={onRecheck}
            loading={query.isFetching}
          >
            {t("connections.ciLogs.recheckButton")}
          </Button>
        </Group>
      </Stack>
    );
  }
  return (
    <CiLogStatusBlock
      status={query.data}
      storedLogin={storedLogin}
      onRecheck={onRecheck}
      isFetching={query.isFetching}
    />
  );
}

/** Сообщения формы: принятые креды, успех, ошибка отказа. */
export function CiLogMessages(props: {
  readonly verifiedLogin: string | null;
  readonly successMessage: string | null;
  readonly errorMessage: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const { verifiedLogin, successMessage, errorMessage } = props;

  return (
    <>
      {verifiedLogin === null ? null : (
        <Alert color="blue">
          {t("connections.ciLogs.verifySuccess", { login: verifiedLogin })}
        </Alert>
      )}
      {successMessage === null ? null : (
        <Alert color="blue">{successMessage}</Alert>
      )}
      {errorMessage === null ? null : (
        <Alert color="red" title={t("connections.errorHeading")}>
          {errorMessage}
        </Alert>
      )}
    </>
  );
}
